import { useEffect, useState } from 'react'
import { useAuth } from '../store/auth.ts'
import { canUsePasskeys } from '../lib/passkeys.ts'
import { ApiError } from '../api/client.ts'
import { PasswordInput } from '../components/PasswordInput.tsx'

type Mode = 'login' | 'register' | 'reset' | 'code'

/**
 * Приглашение из адреса.
 *
 * Ссылку присылают в любом мессенджере, и открывший её должен сразу
 * попасть в нужную форму с заполненным кодом. Иначе по умолчанию
 * предлагается завести свою семью — человек её заводит, а потом сидит
 * в ней один и не понимает, куда делись остальные.
 *
 * Читаем один раз при загрузке, а не внутри страницы: страница входа
 * пересобирается, когда приложение разбирается, вошёл ли уже кто-то, —
 * и прочитанное внутри неё терялось вместе с ней.
 */
const invited = ((): string | null => {
  try {
    const code = new URLSearchParams(location.search).get('invite')
    if (!code) return null

    // Убираем из адреса: код в истории браузера ни к чему,
    // да и «назад» не должно возвращать в ту же форму
    history.replaceState(null, '', location.pathname)
    return code.toUpperCase()
  } catch {
    return null
  }
})()

export function AuthPage() {
  const login = useAuth((s) => s.login)
  const register = useAuth((s) => s.register)
  const resetPassword = useAuth((s) => s.resetPassword)
  const loginByPasskey = useAuth((s) => s.loginByPasskey)
  const loginWithCode = useAuth((s) => s.loginWithCode)

  const [mode, setMode] = useState<Mode>(invited ? 'register' : 'login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [familyName, setFamilyName] = useState('')
  const [inviteCode, setInviteCode] = useState(invited ?? '')
  const [resetCode, setResetCode] = useState('')
  /** Пропуск между паролем и одноразовым кодом */
  const [ticket, setTicket] = useState('')
  const [code, setCode] = useState('')
  const [hasInvite, setHasInvite] = useState(invited !== null)

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  /** Умеет ли это устройство хранить ключ у себя. */
  const [canPasskey, setCanPasskey] = useState(false)

  useEffect(() => {
    void canUsePasskeys().then(setCanPasskey)
  }, [])

  const byPasskey = async () => {
    setBusy(true)
    setError(null)
    try {
      await loginByPasskey()
    } catch (err) {
      /**
       * Отказ человека — не ошибка.
       *
       * Передумал, приложил не тот палец, закрыл окошко — всё это
       * браузер сообщает исключением, но говорить об этом «не вышло
       * войти» нечестно: ничего не ломалось.
       */
      const name = (err as { name?: string }).name
      if (name !== 'NotAllowedError' && name !== 'AbortError') {
        setError(err instanceof ApiError ? err.message : 'Ключ не подошёл')
      }
    } finally {
      setBusy(false)
    }
  }

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    setFieldErrors({})

    try {
      if (mode === 'login') {
        const needCode = await login(email, password)
        if (needCode) {
          setTicket(needCode)
          setMode('code')
          setCode('')
        }
      } else if (mode === 'code') {
        await loginWithCode(ticket, code)
      } else if (mode === 'reset') {
        await resetPassword(resetCode.trim(), password)
      } else {
        await register({
          email,
          password,
          displayName,
          ...(hasInvite ? { inviteCode: inviteCode.trim() } : { familyName: familyName.trim() || undefined }),
        })
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message)
        if (err.fields) {
          setFieldErrors(Object.fromEntries(err.fields.map((f) => [f.field, f.message])))
        }
      } else {
        setError('Нет связи с сервером')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="auth-screen">
      {/**
       * Поля формы названы по-человечески: name и id, а не только
       * autocomplete.
       *
       * Современные браузеры опираются на autocomplete, но менеджеры
       * паролей — особенно в Safari — ищут привычные имена полей. Без
       * них предложение сохранить пароль просто не появляется, и человек
       * остаётся без пароля, даже не заметив этого. Так и вышло при
       * первой регистрации в семье.
       */}
      <form className="auth-card" onSubmit={(e) => void submit(e)}>
        {/* Тот же знак, что на домашнем экране: приложение должно
            узнаваться с первого взгляда, ещё до заголовка */}
        <img className="auth-logo" src="/icons/icon-192.png" alt="" width="64" height="64" />
        <h1 className="auth-title">Семья</h1>
        <p className="auth-lead">
          {mode === 'code'
            ? 'Код из приложения'
            : mode === 'reset'
              ? 'Забыл пароль — бывает'
            : mode === 'login'
              ? 'С возвращением'
              : hasInvite
                ? 'Присоединяйся к своим'
                : 'Заведи общее пространство'}
        </p>

        {mode === 'code' && (
          <label className="field">
            <span className="field-label">Шесть цифр из приложения</span>
            <input
              className="input input-code"
              inputMode="numeric"
              name="otp"
              id="otp"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              autoFocus
              required
            />
            <span className="field-note">
              Код меняется каждые полминуты — если не успел, подойдёт следующий
            </span>
          </label>
        )}

        {mode === 'register' && (
          <label className="field">
            <span className="field-label">Как тебя зовут</span>
            <input
              className="input"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Анна"
              name="displayName"
              id="displayName"
              autoComplete="name"
              required
            />
            {fieldErrors.displayName && <span className="field-error">{fieldErrors.displayName}</span>}
          </label>
        )}

        {mode !== 'reset' && mode !== 'code' && (
        <label className="field">
          <span className="field-label">Почта</span>
          <input
            className="input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="anna@example.com"
            name="email"
            id="email"
            autoComplete="email"
            required
          />
          {fieldErrors.email && <span className="field-error">{fieldErrors.email}</span>}
        </label>
        )}

        {mode === 'reset' && (
          <label className="field">
            <span className="field-label">Код восстановления</span>
            <input
              className="input input-code"
              value={resetCode}
              onChange={(e) => setResetCode(e.target.value.toUpperCase())}
              placeholder="ABCD-EFGH"
              required
            />
            {/* Писем приложение не отправляет — и не будет. Зато рядом
                есть те, кто может выдать код вслух, за столом */}
            <span className="field-note">
              Код выдаёт кто-то из взрослых в семье: у себя в приложении, раздел
              «Семья и приглашения»
            </span>
            {fieldErrors.code && <span className="field-error">{fieldErrors.code}</span>}
          </label>
        )}

        {mode !== 'code' && (
        <label className="field">
          <span className="field-label">{mode === 'reset' ? 'Новый пароль' : 'Пароль'}</span>
          <PasswordInput
            className="input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={mode === 'login' ? '' : 'Не короче 8 символов'}
            name="password"
            id="password"
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            required
          />
          {fieldErrors.password && <span className="field-error">{fieldErrors.password}</span>}
        </label>
        )}

        {mode === 'register' && !hasInvite && (
          <label className="field">
            <span className="field-label">Название семьи</span>
            <input
              className="input"
              value={familyName}
              onChange={(e) => setFamilyName(e.target.value)}
              placeholder="Кимы"
            />
            {/* Своя семья — это отдельное пространство: общего с уже
                заведённым у неё не будет ничего. Сказать об этом
                нужно здесь, а не после */}
            <span className="field-note">
              Заводи семью, только если ты первый. Пригласили — нажми «У меня есть код».
            </span>
          </label>
        )}

        {mode === 'register' && hasInvite && (
          <label className="field">
            <span className="field-label">Код приглашения</span>
            <input
              className="input input-code"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
              placeholder="ABCD-EFGH"
              required
            />
            {fieldErrors.inviteCode && <span className="field-error">{fieldErrors.inviteCode}</span>}
          </label>
        )}

        {error && <div className="auth-error">{error}</div>}

        {/**
         * Вход ключом телефона.
         *
         * Показываем только там, где устройство это умеет, и только при
         * входе: заводить ключ можно лишь изнутри, уже войдя.
         */}
        {mode === 'login' && canPasskey && (
          <button
            type="button"
            className="btn-quiet auth-passkey"
            disabled={busy}
            onClick={() => void byPasskey()}
          >
            Войти лицом или кодом телефона
          </button>
        )}

        <button className="btn-primary" type="submit" disabled={busy}>
          {busy
            ? '…'
            : mode === 'login'
              ? 'Войти'
              : mode === 'code'
                ? 'Подтвердить'
                : mode === 'reset'
                  ? 'Задать пароль'
                  : 'Создать'}
        </button>

        {mode === 'register' && (
          <button
            type="button"
            className="auth-link"
            onClick={() => {
              setHasInvite((v) => !v)
              setError(null)
            }}
          >
            {hasInvite ? 'У меня нет кода, я завожу семью' : 'У меня есть код приглашения'}
          </button>
        )}

        {/* «Забыли пароль?» есть везде, и его ищут глазами именно здесь */}
        {mode === 'login' && (
          <button
            type="button"
            className="auth-link"
            onClick={() => {
              setMode('reset')
              setError(null)
            }}
          >
            Забыл пароль
          </button>
        )}

        <div className="auth-switch">
          {mode === 'code' ? (
            <>
              Не тот вход?{' '}
              <button type="button" className="auth-link" onClick={() => { setMode('login'); setError(null) }}>
                Начать заново
              </button>
            </>
          ) : mode === 'reset' ? (
            <>
              Вспомнил?{' '}
              <button type="button" className="auth-link" onClick={() => { setMode('login'); setError(null) }}>
                Войти
              </button>
            </>
          ) : mode === 'login' ? (
            <>
              Впервые здесь?{' '}
              <button type="button" className="auth-link" onClick={() => { setMode('register'); setError(null) }}>
                Зарегистрироваться
              </button>
            </>
          ) : (
            <>
              Уже есть вход?{' '}
              <button type="button" className="auth-link" onClick={() => { setMode('login'); setError(null) }}>
                Войти
              </button>
            </>
          )}
        </div>
      </form>
    </div>
  )
}
